// To parse this data:
//
//   import { Convert, WorklogsSchema } from "./file";
//
//   const worklogsSchema = Convert.toWorklogsSchema(json);
//
// These functions will throw an error if the JSON doesn't
// match the expected interface, even if the JSON is valid.

/**
 * Worklog schema
 */
export interface WorklogsSchema {
    created_by:    number;
    date_created:  string;
    date_modified: string;
    id?:           number;
    modified_by:   number;
    data:          Data;
    }

export interface Data {
    activities?:         number[];
    areas?:              number[];
    attachments:         AttachmentReference[];
    body:                string;
    comments?:           number[];
    company_id:          number;
    crew_hours:          number;
    crew_size:           number;
    inclement_weather:   InclementWeather;
    presence:            number;
    quantity_items?:     QuantityItem[];
    required_user_id:    number;
    rollover_source_id?: number;
    shift_id?:           number;
    stop_rollover?:      boolean;
    tags:                string[];
    }

export interface AttachmentReference {
    id:   number;
    tags: string[];
    }

export interface InclementWeather {
    crew_hours:  number;
    crew_size:   number;
    description: string;
    is_affected: boolean;
    }

export interface QuantityItem {
    id:         number;
    quantities: number;
    }

// Converts JSON strings to/from your types
// and asserts the results of JSON.parse at runtime
export class Convert {
    public static toWorklogsSchema(json: string): WorklogsSchema {
        return cast(JSON.parse(json), r("WorklogsSchema"));
    }

    public static worklogsSchemaToJson(value: WorklogsSchema): string {
        return JSON.stringify(uncast(value, r("WorklogsSchema")), null, 2);
    }
}

function invalidValue(typ: any, val: any, key: any, parent: any = ''): never {
    const prettyTyp = prettyTypeName(typ);
    const parentText = parent ? ` on ${parent}` : '';
    const keyText = key ? ` for key "${key}"` : '';
    throw Error(`Invalid value${keyText}${parentText}. Expected ${prettyTyp} but got ${JSON.stringify(val)}`);
}

function prettyTypeName(typ: any): string {
    if (Array.isArray(typ)) {
        if (typ.length === 2 && typ[0] === undefined) {
            return `an optional ${prettyTypeName(typ[1])}`;
        } else {
            return `one of [${typ.map(a => { return prettyTypeName(a); }).join(", ")}]`;
        }
    } else if (typeof typ === "object" && typ.literal !== undefined) {
        return typ.literal;
    } else {
        return typeof typ;
    }
}

function jsonToJSProps(typ: any): any {
    if (typ.jsonToJS === undefined) {
        const map: any = {};
        typ.props.forEach((p: any) => map[p.json] = { key: p.js, typ: p.typ });
        typ.jsonToJS = map;
    }
    return typ.jsonToJS;
}

function jsToJSONProps(typ: any): any {
    if (typ.jsToJSON === undefined) {
        const map: any = {};
        typ.props.forEach((p: any) => map[p.js] = { key: p.json, typ: p.typ });
        typ.jsToJSON = map;
    }
    return typ.jsToJSON;
}

function transform(val: any, typ: any, getProps: any, key: any = '', parent: any = ''): any {
    function transformPrimitive(typ: string, val: any): any {
        if (typeof typ === typeof val) return val;
        return invalidValue(typ, val, key, parent);
    }

    function transformUnion(typs: any[], val: any): any {
        // val must validate against one typ in typs
        const l = typs.length;
        for (let i = 0; i < l; i++) {
            const typ = typs[i];
            try {
                return transform(val, typ, getProps);
            } catch (_) {}
        }
        return invalidValue(typs, val, key, parent);
    }

    function transformEnum(cases: string[], val: any): any {
        if (cases.indexOf(val) !== -1) return val;
        return invalidValue(cases.map(a => { return l(a); }), val, key, parent);
    }

    function transformArray(typ: any, val: any): any {
        // val must be an array with no invalid elements
        if (!Array.isArray(val)) return invalidValue(l("array"), val, key, parent);
        return val.map(el => transform(el, typ, getProps));
    }

    function transformDate(val: any): any {
        if (val === null) {
            return null;
        }
        const d = new Date(val);
        if (isNaN(d.valueOf())) {
            return invalidValue(l("Date"), val, key, parent);
        }
        return d;
    }

    function transformObject(props: { [k: string]: any }, additional: any, val: any): any {
        if (val === null || typeof val !== "object" || Array.isArray(val)) {
            return invalidValue(l(ref || "object"), val, key, parent);
        }
        const result: any = {};
        Object.getOwnPropertyNames(props).forEach(key => {
            const prop = props[key];
            const v = Object.prototype.hasOwnProperty.call(val, key) ? val[key] : undefined;
            result[prop.key] = transform(v, prop.typ, getProps, key, ref);
        });
        Object.getOwnPropertyNames(val).forEach(key => {
            if (!Object.prototype.hasOwnProperty.call(props, key)) {
                result[key] = transform(val[key], additional, getProps, key, ref);
            }
        });
        return result;
    }

    if (typ === "any") return val;
    if (typ === null) {
        if (val === null) return val;
        return invalidValue(typ, val, key, parent);
    }
    if (typ === false) return invalidValue(typ, val, key, parent);
    let ref: any = undefined;
    while (typeof typ === "object" && typ.ref !== undefined) {
        ref = typ.ref;
        typ = typeMap[typ.ref];
    }
    if (Array.isArray(typ)) return transformEnum(typ, val);
    if (typeof typ === "object") {
        return typ.hasOwnProperty("unionMembers") ? transformUnion(typ.unionMembers, val)
            : typ.hasOwnProperty("arrayItems")    ? transformArray(typ.arrayItems, val)
            : typ.hasOwnProperty("props")         ? transformObject(getProps(typ), typ.additional, val)
            : invalidValue(typ, val, key, parent);
    }
    // Numbers can be parsed by Date but shouldn't be.
    if (typ === Date && typeof val !== "number") return transformDate(val);
    return transformPrimitive(typ, val);
}

function cast<T>(val: any, typ: any): T {
    return transform(val, typ, jsonToJSProps);
}

function uncast<T>(val: T, typ: any): any {
    return transform(val, typ, jsToJSONProps);
}

function l(typ: any) {
    return { literal: typ };
}

function a(typ: any) {
    return { arrayItems: typ };
}

function u(...typs: any[]) {
    return { unionMembers: typs };
}

function o(props: any[], additional: any) {
    return { props, additional };
}

function m(additional: any) {
    return { props: [], additional };
}

function r(name: string) {
    return { ref: name };
}

const typeMap: any = {
    "WorklogsSchema": o([
        { json: "created_by", js: "created_by", typ: 0 },
        { json: "date_created", js: "date_created", typ: "" },
        { json: "date_modified", js: "date_modified", typ: "" },
        { json: "id", js: "id", typ: u(undefined, 0) },
        { json: "modified_by", js: "modified_by", typ: 0 },
        { json: "data", js: "data", typ: r("Data") },
    ], "any"),
    "Data": o([
        { json: "activities", js: "activities", typ: u(undefined, a(0)) },
        { json: "areas", js: "areas", typ: u(undefined, a(0)) },
        { json: "attachments", js: "attachments", typ: a(r("AttachmentReference")) },
        { json: "body", js: "body", typ: "" },
        { json: "comments", js: "comments", typ: u(undefined, a(0)) },
        { json: "company_id", js: "company_id", typ: 0 },
        { json: "crew_hours", js: "crew_hours", typ: 3.14 },
        { json: "crew_size", js: "crew_size", typ: 0 },
        { json: "inclement_weather", js: "inclement_weather", typ: r("InclementWeather") },
        { json: "presence", js: "presence", typ: 0 },
        { json: "quantity_items", js: "quantity_items", typ: u(undefined, a(r("QuantityItem"))) },
        { json: "required_user_id", js: "required_user_id", typ: 0 },
        { json: "rollover_source_id", js: "rollover_source_id", typ: u(undefined, 0) },
        { json: "shift_id", js: "shift_id", typ: u(undefined, 0) },
        { json: "stop_rollover", js: "stop_rollover", typ: u(undefined, true) },
        { json: "tags", js: "tags", typ: a("") },
    ], "any"),
    "AttachmentReference": o([
        { json: "id", js: "id", typ: 3.14 },
        { json: "tags", js: "tags", typ: a("") },
    ], "any"),
    "InclementWeather": o([
        { json: "crew_hours", js: "crew_hours", typ: 3.14 },
        { json: "crew_size", js: "crew_size", typ: 3.14 },
        { json: "description", js: "description", typ: "" },
        { json: "is_affected", js: "is_affected", typ: true },
    ], "any"),
    "QuantityItem": o([
        { json: "id", js: "id", typ: 0 },
        { json: "quantities", js: "quantities", typ: 3.14 },
    ], "any"),
};
export type WorklogsSchemaWithId = WorklogsSchema & RequiredId
